/**
 * A square button showing a drawn icon: pause, play, sound, muted or back.
 *
 * Icons are drawn as shapes rather than loaded as images, so they stay crisp
 * at any screen size and need no art pipeline. Hover and press match the text
 * buttons in menuButton.ts, and like them the press does not use the scene
 * clock - the pause button has to work while the game's clock is stopped.
 */
import Phaser from 'phaser';
import { PALETTE } from '../../types';
import { BUTTON_FILL, BUTTON_FILL_FOCUS, PRESS_MS, PRESS_SCALE } from './menuButton';

export type IconName = 'pause' | 'play' | 'sound' | 'muted' | 'back';

const INK = 0xf7fbff;
const INK_FOCUS = 0xffc145;

function hexToNumber(hex: string): number {
  return Number.parseInt(hex.replace('#', ''), 16);
}

export class IconButton {
  readonly container: Phaser.GameObjects.Container;
  private readonly bg: Phaser.GameObjects.Graphics;
  private readonly glyph: Phaser.GameObjects.Graphics;
  private readonly hit: Phaser.GameObjects.Rectangle;
  private icon: IconName;
  private hovered = false;
  private pressing = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    icon: IconName,
    onClick: () => void,
    private readonly size = 48,
  ) {
    this.icon = icon;
    this.bg = scene.add.graphics();
    this.glyph = scene.add.graphics();
    const hit = scene.add
      .rectangle(0, 0, size, size, 0xffffff, 0)
      .setInteractive({ useHandCursor: true });
    this.hit = hit;
    this.container = scene.add.container(x, y, [this.bg, this.glyph, hit]);

    hit.on('pointerover', () => {
      this.hovered = true;
      this.draw();
    });
    hit.on('pointerout', () => {
      this.hovered = false;
      this.draw();
    });
    hit.on('pointerup', () => this.press(onClick));
    this.draw();
  }

  setIcon(icon: IconName): this {
    if (icon !== this.icon) {
      this.icon = icon;
      this.draw();
    }
    return this;
  }

  setDepth(depth: number): this {
    this.container.setDepth(depth);
    return this;
  }

  setScrollFactor(factor: number): this {
    this.container.setScrollFactor(factor);
    // Phaser hit-tests a container's child by the child's own scroll factor,
    // not the container's. Without this, a pinned button's hit area drifted
    // off with the camera, and the pause button stopped answering clicks
    // once the climb had scrolled.
    this.hit.setScrollFactor(factor);
    return this;
  }

  destroy(): void {
    this.container.destroy(true);
  }

  private press(onClick: () => void): void {
    if (this.pressing || !this.container.active) return;
    this.pressing = true;
    this.container.setScale(PRESS_SCALE);
    window.setTimeout(() => {
      if (!this.container.active) return;
      this.container.setScale(1);
      this.pressing = false;
      onClick();
    }, PRESS_MS);
  }

  private draw(): void {
    const s = this.size;
    const fill = hexToNumber(this.hovered ? BUTTON_FILL_FOCUS : BUTTON_FILL);
    const ink = this.hovered ? INK_FOCUS : INK;
    // The muted speaker is drawn in the warm warning colour, as the old
    // "Muted" label was, so it reads as "something is off" at a glance.
    const iconInk = this.icon === 'muted' && !this.hovered ? hexToNumber(PALETTE.sunset) : ink;

    this.bg.clear();
    this.bg.fillStyle(fill, 0.92);
    this.bg.fillRoundedRect(-s / 2, -s / 2, s, s, s * 0.24);
    this.bg.lineStyle(2, 0x8ecae6, this.hovered ? 0.9 : 0.45);
    this.bg.strokeRoundedRect(-s / 2, -s / 2, s, s, s * 0.24);

    const g = this.glyph;
    g.clear();
    g.fillStyle(iconInk, 1);
    g.lineStyle(s * 0.075, iconInk, 1);
    switch (this.icon) {
      case 'pause':
        g.fillRoundedRect(-s * 0.2, -s * 0.22, s * 0.14, s * 0.44, s * 0.04);
        g.fillRoundedRect(s * 0.06, -s * 0.22, s * 0.14, s * 0.44, s * 0.04);
        break;
      case 'play':
        g.fillTriangle(-s * 0.13, -s * 0.23, -s * 0.13, s * 0.23, s * 0.24, 0);
        break;
      case 'sound':
        this.speaker(g, s);
        g.beginPath();
        g.arc(s * 0.02, 0, s * 0.13, -0.8, 0.8);
        g.strokePath();
        g.beginPath();
        g.arc(s * 0.02, 0, s * 0.24, -0.8, 0.8);
        g.strokePath();
        break;
      case 'muted':
        this.speaker(g, s);
        // A slash through the speaker, with a gap cut either side of it so it
        // stays legible where it crosses.
        g.lineStyle(s * 0.16, fill, 1);
        g.lineBetween(-s * 0.3, -s * 0.28, s * 0.3, s * 0.28);
        g.lineStyle(s * 0.08, iconInk, 1);
        g.lineBetween(-s * 0.3, -s * 0.28, s * 0.3, s * 0.28);
        break;
      case 'back':
        g.lineBetween(-s * 0.2, 0, s * 0.22, 0);
        g.lineBetween(-s * 0.2, 0, -s * 0.03, -s * 0.17);
        g.lineBetween(-s * 0.2, 0, -s * 0.03, s * 0.17);
        g.fillCircle(-s * 0.2, 0, s * 0.037);
        break;
    }
  }

  private speaker(g: Phaser.GameObjects.Graphics, s: number): void {
    g.fillRect(-s * 0.27, -s * 0.09, s * 0.12, s * 0.18);
    g.fillTriangle(-s * 0.17, -s * 0.09, -s * 0.02, -s * 0.24, -s * 0.02, s * 0.24);
    g.fillTriangle(-s * 0.17, -s * 0.09, -s * 0.17, s * 0.09, -s * 0.02, s * 0.24);
  }
}
